import React from 'react';
import { Chip, FormHelperText, Grid, Stack, TextField, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { useListAllBrokerGroupsQuery } from '@/admin/brokerGroups/api/brokerGroupsApi';
import { AdminFieldLabel } from '@/admin/shared/AdminFieldLabel';
import { AdminBrokerGroupPicker } from '@/admin/shared/pickers/AdminBrokerGroupPicker';
import { USER_FIELD_LABELS } from '../userFormValues';

/**
 * The user drawers' Broker groups section: the primary broker group (chosen only for a new
 * user, canChangePrimary; read only when editing, for now), and the others (kept separate;
 * the primary isn't offered). A user's broker groups share one ledger, so other ledgers'
 * broker groups are disabled, and saved ones on another ledger show in error until they're
 * removed. Neither picker can be cleared all at once: broker groups are removed one chip at
 * a time, and the label's undo icon puts back the starting ones.
 */
export const BrokerGroupFields = ({ canChangePrimary = false }) => {
	const { values, touched, errors, setFieldValue, setValues } = useFormikContext();
	const { data: brokerGroups = [] } = useListAllBrokerGroupsQuery();
	const primaryBrokerGroup = brokerGroups.find(({ id }) => id === values.brokerGroupId);
	const ledger = primaryBrokerGroup?.ledger;
	const fieldError = (name) => touched[name] && errors[name];

	// One setValues, not two setFieldValue calls: each validates with the values from this
	// render, so the second would validate without the new primary and leave the form
	// invalid until another field changed. The primary isn't one of the others.
	const handlePrimaryChange = (brokerGroupId) =>
		setValues({
			...values,
			brokerGroupId: brokerGroupId ?? '',
			brokerGroupArray: values.brokerGroupArray.filter((id) => id !== brokerGroupId),
		});

	return (
		<>
			<Grid size={12}>
				<Typography variant='form_label'>Broker groups</Typography>
			</Grid>
			<Grid size={12}>
				<AdminFieldLabel name='brokerGroupId' required={canChangePrimary}>
					{USER_FIELD_LABELS.brokerGroupId}
				</AdminFieldLabel>
				{canChangePrimary ? (
					<>
						<AdminBrokerGroupPicker
							multiple={false}
							value={values.brokerGroupId || null}
							onChange={handlePrimaryChange}
							disableClearable
							// The chosen broker group, with its ledger as a chip on the right
							renderSingleValue={(brokerGroup) => (
								<Stack
									direction='row'
									alignItems='center'
									justifyContent='space-between'
									spacing={1}
									sx={{ width: '100%', pr: 3 }}
								>
									<Typography noWrap>{brokerGroup.name}</Typography>
									{brokerGroup.ledger && <Chip size='small' label={brokerGroup.ledger} />}
								</Stack>
							)}
							error={Boolean(fieldError('brokerGroupId'))}
						/>
						{fieldError('brokerGroupId') && (
							<FormHelperText error>{fieldError('brokerGroupId')}</FormHelperText>
						)}
					</>
				) : (
					<>
						<TextField
							fullWidth
							value={primaryBrokerGroup?.name ?? ''}
							slotProps={{ input: { readOnly: true } }}
						/>
						<FormHelperText>
							Primary broker group cannot be changed at this time.
						</FormHelperText>
					</>
				)}
			</Grid>
			<Grid size={12}>
				<AdminFieldLabel name='brokerGroupArray'>
					{USER_FIELD_LABELS.brokerGroupArray}
				</AdminFieldLabel>
				<AdminBrokerGroupPicker
					value={values.brokerGroupArray}
					onChange={(ids) => setFieldValue('brokerGroupArray', ids)}
					// Removed one chip at a time, so they can't all go by mistake
					disableClearable
					excludeIds={[values.brokerGroupId].filter(Boolean)}
					showCheckbox
					isOptionDisabled={(option) =>
						Boolean(ledger) &&
						option.ledger !== ledger &&
						!values.brokerGroupArray.includes(option.id)
					}
					error={Boolean(fieldError('brokerGroupArray'))}
				/>
				{fieldError('brokerGroupArray') && (
					<FormHelperText error>{fieldError('brokerGroupArray')}</FormHelperText>
				)}
			</Grid>
		</>
	);
};
